import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { UseOutletContextOrder } from './Order';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { OrderDA } from '../../../../services/admin/order.service';
import { InvoiceDA } from '../../../../services/admin/invoice.service';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Error } from '../../../../models/error.model';
import { CompanyOrders } from '../../../../models/company.orders.model';
import { Order } from '../../../../models/order.model';
import { OrderGroup } from '../../../../models/order.group.model';
import { formatAddress, formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { colorStatusOrder, colorValidUntil } from '../../../../scripts/badge.color.script';
import { accumulatedAmount, isVatSubject } from '../../../../scripts/financial.calculation.script';
import { nameGroup } from '../../../../libraries/group.name.library';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { getError } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';
import { fetchDocumentPdf } from '../../../../scripts/document.pdf.script';
import { usePdfViewer } from '../../../../contexts/PdfViewerContext';
import { sanitizeFilename } from '../../../../scripts/document.filename.script';

import AppSaleListHeader from '../component/SaleListHeader';
import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';

export interface OrderListPageProps {};

let errorResponse: Error, ordersResponse: CompanyOrders;

const OrderListPage: React.FunctionComponent<OrderListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, contactsForCompany} = UseOutletContextOrder()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [button, setButton] = useState({filter: false, group: false})
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])
  const [filterState, setFilterState] = useState({interval: '', start_date: '', end_date: '', contact: '', contact_label: '', validity: '', status: '', expiration: '', payment: '', sort_field: '', sort_mode: '', group: '', search: ''})
  const filterRef = useRef(filterState)
  const filter = filterRef.current
  const setFilter = (v: React.SetStateAction<typeof filterState>) => { const n = typeof v === 'function' ? v(filterRef.current) : v; filterRef.current = n; setFilterState(n) }
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [orderGroups, setOrderGroups] = useState<OrderGroup[] | null>(null)
  const [storage, setStorage] = useState(null)
  const { openUrl } = usePdfViewer();
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const vatSubject = isVatSubject(settingsForCompany, companyForUser);
  // Nombre de colonnes AVANT le bloc montants (TVA/total). La colonne TVA fait partie du bloc montants.
  const COLS_BEFORE_AMOUNTS = 8;

  const executeViewPdf = async (id: string, filename: string) => {
    if (!companyForUser) return;
    setPdfLoading(id);
    try {
      const url = await fetchDocumentPdf(companyForUser.id, 'orders', id);
      openUrl(url, `${sanitizeFilename(filename)}.pdf`);
    } catch {
      // fetchDocumentPdf throws with a readable message on error
    } finally {
      setPdfLoading(null);
    }
  };

  const loadOrders = async (id_company: string, page: number = 1) => {
    const f = filterRef.current
    await OrderDA.getOrders(id_company, f.interval, f.start_date, f.end_date, f.contact, f.status, f.expiration, f.sort_field, f.sort_mode, f.group, page, pagination.limit, f.search).then( (response) => {
      if (response.status === 200) {
        ordersResponse = response.data

        const filterData = {
          company_id: id_company,
          interval: f.interval,
          start_date: f.start_date,
          end_date: f.end_date,
          contact: f.contact,
          contact_label: f.contact_label,
          status: f.status,
          expiration: f.expiration,
          sort_field: f.sort_field,
          sort_mode: f.sort_mode,
          group: f.group,
          search: f.search
        }
        localStorage.setItem('filter_orders', JSON.stringify(filterData))

        const groupBy = filterData.group
        const items = ordersResponse.items ?? ordersResponse.orders ?? []
        const groups = ordersResponse.groups ?? []

        localStorage.setItem('nav_orders', JSON.stringify({ company_id: id_company, ids: items.map((item: any) => item.id) }))

        if (!groupBy || groupBy.length === 0) {
          setOrderGroups([])
          setOrders(items)

          const paginationData = ordersResponse.pagination
          if (paginationData) {
            const totalPages = paginationData.pages || 1
            const clampedPage = page > totalPages && totalPages >= 1 ? totalPages : paginationData.page || page
            let pagesOfPagination: number[] = []
            for (let i = 1; i <= totalPages; i++) { pagesOfPagination.push(i) }
            setPagination({...pagination, current: clampedPage, total: paginationData.total || 0, pages: pagesOfPagination})
            setUrlPage(clampedPage)
            if (page > totalPages && totalPages >= 1) {
              loadOrders(id_company, totalPages)
              return
            }
          }
        } else {
          setOrders([])
          setOrderGroups(groups)
          setPagination({current: 1, limit: 20, total: 0, pages: [1]})
          setUrlPage(1)
        }

        setButton({filter: Boolean(filterData.interval || filterData.start_date || filterData.end_date || filterData.contact || filterData.status || filterData.expiration), group: Boolean(groupBy)})
        setStorage((localStorage.getItem('filter_orders')) ? JSON.parse(localStorage.getItem('filter_orders')!) : null)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      setUrlPage(1)
      loadOrders(companyForUser.id, 1)
    }
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadOrders(companyForUser.id, current)
  }

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId)
      }
      return [...prev, orderId]
    })
  }

  const handleSelectAll = () => {
    const facturableOrders = (orders ?? []).filter(item => {
      const status = normalizeStatus(item.status)
      return ['approved'].includes(status)
    }).map(item => item.id)

    if (selectedOrders.length === facturableOrders.length) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(facturableOrders)
    }
  }

  const submitCreateMultiInvoice = async () => {
    if (!companyForUser || selectedOrders.length === 0) return

    const result = await GlobalSwal.fire({
      title: lang.labels.createInvoiceFromSelection,
      text: `${selectedOrders.length} ${lang.labels.ordersWillBeInvoicedTogether}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesCreate,
      cancelButtonText: lang.labels.noCancel,
      customClass: {confirmButton:'btn btn-primary', cancelButton:'btn btn-secondary'}
    })

    if (result.isConfirmed) {
      await InvoiceDA.createInvoiceFromOrders(companyForUser.id, selectedOrders).then( (response) => {
        if (response.status === 200 || response.status === 201) {
          const invoice = response.data
          setSelectedOrders([])
          navigate(`/main/sale/invoice/view/${invoice.id}`)
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      })
    }
  }

  useEffect( () => {
    let filter_orders: any = (localStorage.getItem('filter_orders')) ? JSON.parse(localStorage.getItem('filter_orders')!) : null

    if (companyForUser) {
      const isMatch = filter_orders && filter_orders.company_id === companyForUser.id
      setFilter({
        interval: isMatch ? (filter_orders.interval ?? '') : '', start_date: isMatch ? (filter_orders.start_date ?? '') : '', end_date: isMatch ? (filter_orders.end_date ?? '') : '',
        contact: isMatch ? (filter_orders.contact ?? '') : '', contact_label: isMatch ? (filter_orders.contact_label ?? '') : '', validity: '', status: isMatch ? (filter_orders.status ?? '') : '', expiration: isMatch ? (filter_orders.expiration ?? '') : '',
        payment: '', sort_field: isMatch ? (filter_orders.sort_field ?? '') : '', sort_mode: isMatch ? (filter_orders.sort_mode ?? '') : '', group: isMatch ? (filter_orders.group ?? '') : '',
        search: isMatch ? (filter_orders.search ?? '') : ''
      })

      setOrders(null)
      setOrderGroups(null)
      setButton({filter: false, group: false})
      loadOrders(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.orders, branch: lang.labels.listOrders}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && orders && orderGroups
        ?
        <div className="card card-flush">
          <AppSaleListHeader sale={"order"} storage={storage} button={button} filter={filter} records={orders} recordGroups={orderGroups} setFilter={setFilter} loadDocuments={loadOrders} totalItems={!button.group ? pagination.total : undefined}></AppSaleListHeader>
          { selectedOrders.length > 0 &&
            <div className="card-scroll d-flex align-items-center bg-light-success rounded border-0 p-3 mx-7 mb-4">
              <span className="fw-bold me-3">{selectedOrders.length} {lang.labels.ordersSelected}</span>
              <button className="btn btn-sm btn-primary" type="button" onClick={submitCreateMultiInvoice}>
                {lang.labels.createInvoiceFromSelection}
              </button>
            </div>
          }
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      { !button.group &&
                        <th className="w-30px">
                          <input type="checkbox" className="form-check-input"
                            checked={selectedOrders.length > 0 && selectedOrders.length === (orders ?? []).filter(item => ['approved'].includes(normalizeStatus(item.status))).length}
                            onChange={handleSelectAll} />
                        </th>
                      }
                      <th className="w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created_date")}>
                          <span className="me-1">{lang.labels.documentDate}</span>
                          <AppSortTable attribute={"created_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("order_number")}>
                          <span className="me-1">{lang.labels.orderNumber}</span>
                          <AppSortTable attribute={"order_number"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("concerning")}>
                          <span className="me-1">{lang.labels.concerning}</span>
                          <AppSortTable attribute={"concerning"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("delivery_address")}>
                          <span className="me-1">{lang.labels.deliveyAddress}</span>
                          <AppSortTable attribute={"delivery_address"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("customer")}>
                          <span className="me-1">{lang.labels.customer}</span>
                          <AppSortTable attribute={"customer"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("expiration")}>
                          <span className="me-1">{lang.labels.expiration}</span>
                          <AppSortTable attribute={"expiration"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      {vatSubject && <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.tax_total")}>
                          <span className="me-1">{lang.labels.vatAmount}</span>
                          <AppSortTable attribute={"amount.tax_total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>}
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.total")}>
                          <span className="me-1">{lang.labels.amountTotal}</span>
                          <AppSortTable attribute={"amount.total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-50px text-center"></th>
                    </tr>
                  </thead>
                  { orders.length > 0 || orderGroups.length > 0
                    ?
                    <>
                      { button.group
                        ?
                        <>
                          { orderGroups.map (( (item, index) => { return (
                            <tbody key={index} className="text-secondary fw-bold">
                              <tr>
                                <td colSpan={10 + (vatSubject ? 1 : 0)} className="text-uppercase fw-bolder fs-7">
                                  <div className="d-flex align-items-center">
                                    <span className="svg-icon svg-icon-3 svg-icon-gray-900 me-5">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M22 12C22 12.9 21.9 13.8 21.7 14.6L5 4.89999C6.8 3.09999 9.3 2 12 2C17.5 2 22 6.5 22 12Z" fill="black"/>
                                        <path opacity="0.3" d="M3.7 17.5C2.6 15.9 2 14 2 12C2 9.20003 3.1 6.70002 5 4.90002L9.3 7.40002V14.2L3.7 17.5ZM17.2 12L5 19.1C6.8 20.9 9.3 22 12 22C16.6 22 20.5 18.8 21.7 14.6L17.2 12Z" fill="black"/>
                                      </svg>
                                    </span>
                                    <span className="me-3">{lang.labels.group} {index + 1}:</span>
                                    {nameGroup(lang, filter.group, item.group_detail)}
                                  </div>
                                </td>
                              </tr>
                              { item.orders.map (( (sub_item, sub_index) => { return (
                                <tr key={sub_index} className="row-dashed">
                                  <td></td>
                                  <td className="text-nowrap">{dateFormat(lang.code, sub_item.created_date)}</td>
                                  <td>
                                    <Link to={"/main/sale/order/view/" + sub_item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{sub_item.order_number}</Link>
                                  </td>
                                  <td>{sub_item.concerning}</td>
                                  <td>{formatAddress(lang, sub_item.delivery_address?.address ?? sub_item.delivery_address)}</td>
                                  <td>{formatContact(sub_item.customer)}</td>
                                  <td>
                                    <span className={`badge badge-light-${colorStatusOrder(sub_item.status)} fs-7`}>{statusDocumentValue(lang, sub_item.status)}</span>
                                  </td>
                                  <td>
                                    <span className={`badge badge-light-${colorValidUntil(sub_item.status, sub_item.valid_until)} fs-7`}>{dateFormat(lang.code, sub_item.valid_until)}</span>
                                  </td>
                                  {vatSubject && <td className="text-end text-nowrap">{sub_item.currency.code} {formatMoney(sub_item.amount?.tax_total ?? 0)}</td>}
                                  <td className="text-end text-nowrap">
                                    {sub_item.currency.code} {formatMoney(sub_item.amount.total)}
                                    {sub_item.foreign_currency?.code && <div className="fs-9 fw-normal text-muted">{sub_item.foreign_currency.code} {formatMoney(sub_item.foreign_currency.amount?.total ?? 0)}</div>}
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-icon btn-light-primary"
                                      title={lang.labels.view}
                                      onClick={() => executeViewPdf(sub_item.id, sub_item.order_number)}
                                      disabled={pdfLoading === sub_item.id}
                                    >
                                      {pdfLoading === sub_item.id
                                        ? <span className="spinner-border spinner-border-sm"></span>
                                        : <i className="bi bi-eye fs-5"></i>
                                      }
                                    </button>
                                  </td>
                                </tr>
                              )}))}
                              <tr className={`fw-bolder fs-7 ${index < orderGroups.length - 1 && "row-dashed"}`}>
                                <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                                {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.orders).tax)}</td>}
                                <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(item.orders).total)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          )}))}
                        </>
                        :
                        <>
                          <tbody className="text-secondary fw-bold">
                            { orders.map (( (item, index) => { return (
                              <tr key={index} className="row-dashed">
                                <td>
                                  { ['approved'].includes(normalizeStatus(item.status)) &&
                                    <input type="checkbox" className="form-check-input"
                                      checked={selectedOrders.includes(item.id)}
                                      onChange={() => handleSelectOrder(item.id)} />
                                  }
                                </td>
                                <td className="text-nowrap">{dateFormat(lang.code, item.created_date)}</td>
                                <td>
                                  <Link to={"/main/sale/order/view/" + item.id}className="fw-bolder text-nowrap text-dark text-hover-primary">{item.order_number}</Link>
                                </td>
                                <td>{item.concerning}</td>
                                <td>{formatAddress(lang, item.delivery_address?.address ?? item.delivery_address ?? (item.customer?.data ?? item.customer)?.address)}</td>
                                <td>{formatContact(item.customer)}</td>
                                <td>
                                  <span className={`badge badge-light-${colorStatusOrder(item.status)} fs-7`}>{statusDocumentValue(lang, item.status)}</span>
                                </td>
                                <td>
                                  <span className={`badge badge-light-${colorValidUntil(item.status, item.valid_until)} fs-7`}>{dateFormat(lang.code, item.valid_until)}</span>  
                                </td>
                                {vatSubject && <td className="text-end text-nowrap">{item.currency.code} {formatMoney(item.amount?.tax_total ?? 0)}</td>}
                                <td className="text-end text-nowrap">
                                  {item.currency.code} {formatMoney(item.amount.total)}
                                  {item.foreign_currency?.code && <div className="fs-9 fw-normal text-muted">{item.foreign_currency.code} {formatMoney(item.foreign_currency.amount?.total ?? 0)}</div>}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-primary"
                                    title={lang.labels.view}
                                    onClick={() => executeViewPdf(item.id, item.order_number)}
                                    disabled={pdfLoading === item.id}
                                  >
                                    {pdfLoading === item.id
                                      ? <span className="spinner-border spinner-border-sm"></span>
                                      : <i className="bi bi-eye fs-5"></i>
                                    }
                                  </button>
                                </td>
                              </tr>
                            )}))}
                          </tbody>
                          <tfoot>
                            <tr className="fw-bolder fs-7">
                              <td colSpan={COLS_BEFORE_AMOUNTS}></td>
                              {vatSubject && <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(orders).tax)}</td>}
                              <td className="text-end text-nowrap">{companyForUser.currency?.code ?? ''} {formatMoney(accumulatedAmount(orders).total)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </>
                      }
                    </>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={10 + (vatSubject ? 1 : 0)} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              {!button.group && <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>}
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default OrderListPage;
